export type LogKind = 'ok' | 'warn' | 'err';

import { getActiveChainConfig } from './config';

type ToastRefs = {
  root: HTMLDivElement;
  icon: HTMLSpanElement;
  text: HTMLSpanElement;
  link: HTMLAnchorElement;
};

const TX_HASH_REGEX = /0x[a-fA-F0-9]{64}/;

let toastRefs: ToastRefs | null = null;
let toastTimer: number | null = null;
let toastHideTimer: number | null = null;

function ensureToastRefs(): ToastRefs {
  if (toastRefs) return toastRefs;

  const host = document.createElement('div');
  host.className = 'bb-toast-host';

  const root = document.createElement('div');
  root.className = 'bb-toast';
  root.hidden = true;
  root.setAttribute('role', 'status');
  root.setAttribute('aria-live', 'polite');

  const icon = document.createElement('span');
  icon.className = 'bb-toast-icon';
  icon.setAttribute('aria-hidden', 'true');
  icon.textContent = 'i';

  const text = document.createElement('span');
  text.className = 'bb-toast-text';

  const link = document.createElement('a');
  link.className = 'bb-toast-link';
  link.hidden = true;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.textContent = 'View tx';

  root.appendChild(icon);
  root.appendChild(text);
  root.appendChild(link);
  host.appendChild(root);
  document.body.appendChild(host);

  toastRefs = { root, icon, text, link };
  return toastRefs;
}

function getExplorerTxUrl(message: string): string | null {
  const txHash = message.match(TX_HASH_REGEX)?.[0];
  if (!txHash) return null;
  const explorerBase = (getActiveChainConfig().blockExplorerUrls?.[0] || '').replace(/\/+$/, '');
  if (!explorerBase) return null;
  return `${explorerBase}/tx/${txHash}`;
}

function showToast(kind: LogKind, message: string): void {
  const refs = ensureToastRefs();

  if (toastTimer !== null) {
    window.clearTimeout(toastTimer);
    toastTimer = null;
  }
  if (toastHideTimer !== null) {
    window.clearTimeout(toastHideTimer);
    toastHideTimer = null;
  }

  refs.root.classList.remove('is-success', 'is-error', 'is-warning', 'is-info', 'is-visible');

  if (kind === 'ok') {
    refs.icon.textContent = '+';
    refs.root.classList.add('is-success');
  } else if (kind === 'err') {
    refs.icon.textContent = 'x';
    refs.root.classList.add('is-error');
  } else {
    refs.icon.textContent = '!';
    refs.root.classList.add('is-warning');
  }

  refs.text.textContent = message;

  const txHref = getExplorerTxUrl(message);
  if (txHref) {
    refs.link.href = txHref;
    refs.link.hidden = false;
  } else {
    refs.link.hidden = true;
    refs.link.removeAttribute('href');
  }

  refs.root.hidden = false;
  void refs.root.offsetWidth;
  refs.root.classList.add('is-visible');

  const timeoutMs = txHref ? 6500 : 2200;
  toastTimer = window.setTimeout(() => {
    refs.root.classList.remove('is-visible');
    toastHideTimer = window.setTimeout(() => {
      refs.root.hidden = true;
    }, 180);
  }, timeoutMs);
}

export function logLine(container: HTMLElement, kind: LogKind, text: string): void {
  void container;
  showToast(kind, text);
}
